import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft, AudioLines, BarChart3, Captions, Check, Clock3, Gauge, GaugeCircle, ListTree, ListVideo,
  Maximize2, Pause, Play, RefreshCw, RotateCcw, RotateCw, Server, SkipForward, TimerReset,
  Sparkles, TriangleAlert, X,
} from 'lucide-react'
import { applyFocus } from '../hooks/useRemoteNavigation'
import { APP_VERSION } from '../constants'
import { currentTvModel, diagnosticQr } from '../lib/diagnostics'
import { formatBytesPerSecond, formatFileSize, formatTime } from '../lib/format'
import {
  assessStartupHealth, bufferedMediaState, classifyMediaError, degradedVideoOutput, frozenVideoOutput,
  mediaUrlExpiresAt, parseRuntimeSeconds, playbackRecoveryAction, prematureMediaEnd, readVideoFrameCounters,
  shouldRebuildAfterSuspend, stableDownloadSpeed, startupBufferTarget, type PlaybackRecoveryCause,
  type StartupHealth, type VideoProgressSample,
} from '../lib/playback'
import { getCompatibilityEvidence, recordCompatibilityOutcome } from '../lib/compatibility'
import { getMeasuredSpeed, saveMeasuredSpeed } from '../lib/storage'
import type { AdminConfig } from '../lib/config'
import { getEngineStats, getServerAudioTracks, prepareStream, type ServerAudioTrack } from '../lib/stremio'
import { getStreamsFromAddons, getSubtitlesFromAddons } from '../lib/addons'
import type { CodecCapabilities } from '../lib/codecs'
import { hdrStatusNote, hdrSupport, type HdrCapabilities } from '../lib/hdr'
import {
  boundedRemoteNumber, boundedRemoteText, isRemotePlayerCommand, streamRemoteKey,
  toRemoteMediaSummary, type RemotePlayerState,
} from '../lib/remoteProtocol'
import { findRefreshedStream, getStreamAudio, getStreamCodec, getStreamContainer, getStreamHdr, getStreamResolution, getStreamSize, preferredTransport, rankStreams, streamCompatibilityKey, streamTransportCompatibilityKey, type RankedStream } from '../lib/streams'
import { languageMatches, languageName, rankSubtitles, subtitleBlobUrl } from '../lib/subtitles'
import type { EngineStats, Meta, PlayableStream, Settings, StremioStream, Subtitle, Video } from '../types'

interface Props {
  stream: StremioStream
  meta: Meta
  video?: Video
  settings: Settings
  hdrCapabilities: HdrCapabilities
  codecCapabilities: CodecCapabilities
  resumeAt?: number
  resumePlaying?: boolean
  playbackSessionKey: number
  onClose: () => void
  onSwitchSource: (stream: StremioStream, currentTime: number, playing?: boolean) => void
  onExitToSources: () => void
  onEnded: () => void
  onPlayEpisode: (video: Video) => void
  onSettingsChange: (settings: Settings) => void
  onProgress: (currentTime: number, duration: number, playing?: boolean) => void
  onRemoteState: (state: RemotePlayerState | undefined) => void
  config: AdminConfig
}

type PlayerMenu = 'sources' | 'subtitles' | 'audio' | 'episodes' | 'chapters' | 'speed' | 'picture' | 'sleep' | 'stats' | null
type PictureMode = 'fit' | 'fill' | 'zoom'
type SleepChoice = 0 | 15 | 30 | 60 | 'end'
const EPISODE_MENU_PAGE_SIZE = 40

interface AudioTrackLike {
  id?: string
  label?: string
  language?: string
  enabled: boolean
}

interface AudioTrackListLike {
  length: number
  [index: number]: AudioTrackLike
}

interface PlayerAudioTrack {
  index: number
  id: string
  label: string
  language: string
  enabled: boolean
}

function episodeText(video?: Video, fallback = 'Episode') {
  return video?.season && video?.episode ? `S${video.season} E${video.episode}` : fallback
}

function readAudioTracks(element: HTMLVideoElement | null): PlayerAudioTrack[] {
  try {
    const list = (element as HTMLVideoElement & { audioTracks?: AudioTrackListLike } | null)?.audioTracks
    if (!list) return []
    const length = Math.max(0, Math.min(24, list.length || 0))
    const tracks: PlayerAudioTrack[] = []
    for (let index = 0; index < length; index += 1) {
      const track = list[index]
      if (!track) continue
      tracks.push({
        index,
        id: track.id || String(index),
        label: track.label || (track.language ? languageName(track.language) : `Audio ${index + 1}`),
        language: track.language || '',
        enabled: track.enabled,
      })
    }
    return tracks
  } catch {
    // Native webOS lists can detach between length and indexed reads while the
    // media pipeline is being replaced. The next one-second poll will retry.
    return []
  }
}

export function Player({
  stream, meta, video, settings, hdrCapabilities, codecCapabilities, resumeAt = 0, resumePlaying = true, playbackSessionKey, onClose, onSwitchSource, onExitToSources, onEnded,
  onPlayEpisode, onSettingsChange, onProgress, onRemoteState, config,
}: Props) {
  const playerRootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hideTimer = useRef<number>()
  const oledTimer = useRef<number>()
  const chromeVisible = useRef(true)
  const scrubTo = useRef<number | null>(null)
  const scrubStep = useRef({ at: 0, step: 10, direction: 0 })
  const commitTimer = useRef<number>()
  const lastSaved = useRef(0)
  const triedFallback = useRef(false)
  const fallbackOrigin = useRef<{ url: string; transport: 'hls' | 'direct'; switchedAt: number } | null>(null)
  const restoredProgress = useRef(false)
  const subtitleUrlRef = useRef('')
  const subtitleToken = useRef(0)
  const automaticSubtitle = useRef(false)
  const timelineRef = useRef({ current: 0, duration: 0 })
  const progressCallbackRef = useRef(onProgress)
  const remoteStateCallback = useRef(onRemoteState)
  const compatibilityRecorded = useRef(false)
  const successfulTransport = useRef<'hls' | 'direct' | ''>('')
  const audioPreferenceApplied = useRef(false)
  const hardwareSeek = useRef({ at: 0, step: 30, direction: 0 })
  const feedbackTimer = useRef<number>()
  const autoplayCancelled = useRef(false)
  const serverAudioProbe = useRef('')
  const pendingServerAudio = useRef<number | null>(null)
  const pipelineResume = useRef<number | null>(null)
  const desiredPlaying = useRef(resumePlaying)
  const mediaIdentity = useRef('')
  const waitingSince = useRef(0)
  const lastProgressAt = useRef(Date.now())
  const lastProgressTime = useRef(0)
  const recoveryInFlight = useRef(false)
  const recoveryHandler = useRef<(cause: PlaybackRecoveryCause, target?: number) => void>(() => undefined)
  const recoverySourceKey = useRef('')
  const sourceRefreshAttempts = useRef(0)
  const sourceRefreshToken = useRef(0)
  const reloadAttempts = useRef(0)
  const lastRecoveryAt = useRef(0)
  const pendingOfflineRecovery = useRef<{ cause: PlaybackRecoveryCause; target?: number } | null>(null)
  const prepareToken = useRef(0)
  const seekTimer = useRef<number>()
  const seekTarget = useRef<number | null>(null)
  const videoOutputTimer = useRef<number>()
  const audioRestore = useRef<{ index: number; language: string } | null>(null)
  const proactiveRefreshUrl = useRef('')
  const videoProgressSample = useRef<VideoProgressSample | null>(null)
  const hiddenAt = useRef(0)
  const resumeWhenVisible = useRef(false)
  const maximumDurationSeen = useRef(0)
  const remoteStateTimer = useRef<number>()
  const pendingRemoteState = useRef<RemotePlayerState>()
  const publishedRemoteStatus = useRef<RemotePlayerState['status']>()

  const [prepared, setPrepared] = useState<PlayableStream | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [playing, setPlaying] = useState(false)
  const [controls, setControls] = useState(true)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [bufferedAhead, setBufferedAhead] = useState(0)
  const [stats, setStats] = useState<EngineStats>({})
  const [speedSamples, setSpeedSamples] = useState<number[]>([])
  const [mediaReady, setMediaReady] = useState(false)
  const [healthDeadlineReached, setHealthDeadlineReached] = useState(false)
  const [startupBufferDeadlineReached, setStartupBufferDeadlineReached] = useState(false)
  const [startupAllowed, setStartupAllowed] = useState(false)
  const [startupWarning, setStartupWarning] = useState<StartupHealth | null>(null)
  const [scrubPreview, setScrubPreview] = useState<number | null>(null)

  const [menu, setMenu] = useState<PlayerMenu>(null)
  const [subtitles, setSubtitles] = useState<Subtitle[]>([])
  const [subtitleLoading, setSubtitleLoading] = useState(false)
  const [subtitleError, setSubtitleError] = useState('')
  const [activeSubtitle, setActiveSubtitle] = useState<Subtitle | null>(null)
  const [subtitleUrl, setSubtitleUrl] = useState('')
  const [audioTracks, setAudioTracks] = useState<PlayerAudioTrack[]>([])
  const [playbackRate, setPlaybackRate] = useState(1)
  const [picture, setPicture] = useState<PictureMode>('fit')
  const [sleep, setSleep] = useState<SleepChoice>(0)
  const [sleepDeadline, setSleepDeadline] = useState(0)
  const [sleepRemaining, setSleepRemaining] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [oledSafe, setOledSafe] = useState(false)
  const [nextCountdown, setNextCountdown] = useState<number | null>(null)
  const [diagnosticImage, setDiagnosticImage] = useState('')
  const [sourceChoices, setSourceChoices] = useState<RankedStream[]>([])
  const [sourceVisibleCount, setSourceVisibleCount] = useState(24)
  const [episodePage, setEpisodePage] = useState(0)
  const [sourceLoading, setSourceLoading] = useState(false)
  const [sourceError, setSourceError] = useState('')
  const [serverAudioTracks, setServerAudioTracks] = useState<ServerAudioTrack[]>([])
  const [serverAudioLoading, setServerAudioLoading] = useState(false)
  const [serverAudioError, setServerAudioError] = useState('')
  const [mediaAttempt, setMediaAttempt] = useState(0)
  const [prepareAttempt, setPrepareAttempt] = useState(0)
  const [recoveryMessage, setRecoveryMessage] = useState('')
  timelineRef.current = { current, duration }
  progressCallbackRef.current = onProgress
  remoteStateCallback.current = onRemoteState

  const persistProgress = useCallback(() => {
    const timeline = timelineRef.current
    const element = videoRef.current
    const elementCurrent = element?.currentTime
    const elementDuration = element?.duration
    const exactCurrent = typeof elementCurrent === 'number' && Number.isFinite(elementCurrent)
      ? elementCurrent : timeline.current
    // HLS can temporarily report Infinity while its VOD manifest is opening.
    // Never discard a previously known finite duration during lifecycle save.
    const exactDuration = typeof elementDuration === 'number' && Number.isFinite(elementDuration) && elementDuration > 0
      ? elementDuration : timeline.duration
    if (exactCurrent > 0 && exactDuration > 0 && Number.isFinite(exactDuration)) {
      progressCallbackRef.current(exactCurrent, exactDuration, desiredPlaying.current)
    }
  }, [])

  const closePlayer = useCallback(() => {
    persistProgress()
    onClose()
  }, [onClose, persistProgress])

  const chooseAnotherSource = useCallback(() => {
    chromeVisible.current = true
    setControls(true)
    setMenu('sources')
    window.clearTimeout(hideTimer.current)
  }, [])

  useEffect(() => {
    const root = playerRootRef.current
    if (!root) return
    let frame = 0
    const pin = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        if (root.scrollTop) root.scrollTop = 0
        if (root.scrollLeft) root.scrollLeft = 0
      })
    }
    root.addEventListener('scroll', pin)
    root.addEventListener('focusin', pin)
    return () => {
      window.cancelAnimationFrame(frame)
      root.removeEventListener('scroll', pin)
      root.removeEventListener('focusin', pin)
    }
  }, [])

  const title = video?.name || video?.title || meta.name
  const label = episodeText(video, meta.type === 'movie' ? 'Movie' : 'Episode')
  const streamHdr = getStreamHdr(stream)
  const streamHdrSupport = hdrSupport(streamHdr, hdrCapabilities)
  const videos = useMemo(() => (meta.videos || []).filter((item) => item.season !== 0), [meta.videos])
  const currentVideoIndex = videos.findIndex((item) => item.id === video?.id)
  const nextVideo = currentVideoIndex >= 0 ? videos[currentVideoIndex + 1] : undefined
  const chapters = video?.chapters || []
  const introVisible = typeof video?.introEnd === 'number'
    && current >= (video.introStart || 0) && current < video.introEnd
  const creditsVisible = typeof video?.creditsStart === 'number' && current >= video.creditsStart
  const subtitleChoices = useMemo(() => rankSubtitles(subtitles, settings.subtitleLanguage, 2, {
    forced: settings.subtitleForced,
    sdh: settings.subtitleSdh,
  }), [settings.subtitleForced, settings.subtitleLanguage, settings.subtitleSdh, subtitles])

  const showControls = useCallback(() => {
    const wasHidden = !chromeVisible.current
    chromeVisible.current = true
    setControls(true)
    window.clearTimeout(hideTimer.current)
    if (!menu) {
      hideTimer.current = window.setTimeout(() => {
        chromeVisible.current = false
        setControls(false)
      }, Math.max(1, config.playback.chromeHideSeconds) * 1000)
    }
    return wasHidden
  }, [config.playback.chromeHideSeconds, menu])

  const showFeedback = useCallback((message: string) => {
    setFeedback(message)
    window.clearTimeout(feedbackTimer.current)
    feedbackTimer.current = window.setTimeout(() => setFeedback(''), 1600)
  }, [])

  const openMenu = useCallback((value: Exclude<PlayerMenu, null>) => {
    chromeVisible.current = true
    setControls(true)
    if (value === 'episodes') setEpisodePage(Math.max(0, Math.floor(Math.max(0, currentVideoIndex) / EPISODE_MENU_PAGE_SIZE)))
    setMenu(value)
    window.clearTimeout(hideTimer.current)
  }, [currentVideoIndex])

  const closeMenu = useCallback(() => {
    const closing = menu
    setMenu(null)
    window.setTimeout(() => {
      applyFocus(
        document.querySelector<HTMLElement>(`.player-secondary [data-player-menu="${closing}"]`)
        || document.querySelector<HTMLElement>('.player-secondary [data-player-menu-button]'),
      )
    }, 0)
  }, [menu])

  const playElement = useCallback((element: HTMLVideoElement) => {
    const started = element.play()
    started?.catch((reason: unknown) => {
      if (element !== videoRef.current) return
      const name = reason instanceof DOMException ? reason.name
        : reason && typeof reason === 'object' && 'name' in reason ? String(reason.name) : ''
      if (name === 'AbortError') return
      if (name === 'NotAllowedError') {
        desiredPlaying.current = false
        setLoading(false)
        showFeedback('Press Play to continue')
        showControls()
        return
      }
      recoveryHandler.current('media-error')
    })
  }, [showControls, showFeedback])

  useEffect(() => {
    if (!menu) return
    const timer = window.setTimeout(() => applyFocus(
      document.querySelector<HTMLElement>('.player-menu [data-autofocus]')
      || document.querySelector<HTMLElement>('.player-menu [data-focusable]'),
    ), 60)
    return () => clearTimeout(timer)
  }, [menu])

  useEffect(() => {
    if (menu !== 'sources') return
    let active = true
    setSourceLoading(true)
    setSourceError('')
    setSourceChoices([])
    setSourceVisibleCount(24)
    getStreamsFromAddons(settings, meta.type, video?.id || meta.id)
      .then((values) => {
        if (!active) return
        const ranked = rankStreams(values, {
          runtime: meta.runtime,
          durationSeconds: timelineRef.current.duration || undefined,
          measuredSpeed: getMeasuredSpeed(),
          hdrCapabilities,
          hdrMode: settings.hdrMode,
          codecCapabilities,
          compatibility: getCompatibilityEvidence(),
        })
        setSourceChoices(ranked)
        if (!ranked.length) setSourceError('No sources were returned for this title.')
      })
      .catch((reason) => active && setSourceError(reason instanceof Error ? reason.message : 'Could not load sources.'))
      .finally(() => active && setSourceLoading(false))
    return () => { active = false }
  }, [codecCapabilities, hdrCapabilities, menu, meta.id, meta.runtime, meta.type, settings, video?.id])

  const startPreparedPlayback = useCallback(() => {
    const element = videoRef.current
    if (!element) return
    setStartupWarning(null)
    setStartupAllowed(true)
    setLoading(false)
    setError('')
    if (desiredPlaying.current && document.visibilityState !== 'hidden') {
      playElement(element)
    } else {
      element.pause()
      setPlaying(false)
    }
    showControls()
  }, [playElement, showControls])

  const requestPlay = useCallback(() => {
    desiredPlaying.current = true
    const element = videoRef.current
    if (element && startupAllowed) playElement(element)
  }, [playElement, startupAllowed])

  const requestPause = useCallback(() => {
    desiredPlaying.current = false
    videoRef.current?.pause()
  }, [])

  const togglePlay = useCallback(() => {
    const element = videoRef.current
    if (!element || !startupAllowed) return
    if (element.paused) requestPlay()
    else requestPause()
    showControls()
  }, [requestPause, requestPlay, showControls, startupAllowed])

  const clearSeekWatchdog = useCallback(() => {
    window.clearTimeout(seekTimer.current)
    seekTimer.current = undefined
    seekTarget.current = null
  }, [])

  const requestSeek = useCallback((target: number, reveal = true) => {
    const element = videoRef.current
    if (!element || !Number.isFinite(target)) return
    const bounded = Math.max(0, Math.min(element.duration || target, target))
    videoProgressSample.current = null
    seekTarget.current = bounded
    window.clearTimeout(seekTimer.current)
    try {
      element.currentTime = bounded
    } catch {
      recoveryHandler.current('seek-timeout', bounded)
      return
    }
    seekTimer.current = window.setTimeout(() => {
      const currentElement = videoRef.current
      if (!currentElement || seekTarget.current === null) return
      const unfinished = currentElement.seeking || Math.abs((currentElement.currentTime || 0) - seekTarget.current) > 2
      if (unfinished) recoveryHandler.current('seek-timeout', seekTarget.current)
      else clearSeekWatchdog()
    }, 6_500)
    if (reveal) showControls()
  }, [clearSeekWatchdog, showControls])

  const commitScrub = useCallback(() => {
    window.clearTimeout(commitTimer.current)
    const element = videoRef.current
    if (element && scrubTo.current !== null) requestSeek(scrubTo.current)
    scrubTo.current = null
    scrubStep.current = { at: 0, step: 10, direction: 0 }
    setScrubPreview(null)
  }, [requestSeek])

  const cancelScrub = useCallback(() => {
    window.clearTimeout(commitTimer.current)
    scrubTo.current = null
    scrubStep.current = { at: 0, step: 10, direction: 0 }
    setScrubPreview(null)
  }, [])

  const scrub = useCallback((direction: number) => {
    const element = videoRef.current
    if (!element || !element.duration) return
    const now = Date.now()
    const accelerating = scrubStep.current.direction === direction && now - scrubStep.current.at < 400
    scrubStep.current = {
      at: now,
      step: accelerating ? Math.min(120, scrubStep.current.step * 2) : 10,
      direction,
    }
    const base = scrubTo.current === null ? element.currentTime : scrubTo.current
    const next = Math.max(0, Math.min(element.duration, base + direction * scrubStep.current.step))
    scrubTo.current = next
    setScrubPreview(next)
    window.clearTimeout(commitTimer.current)
    commitTimer.current = window.setTimeout(commitScrub, 900)
  }, [commitScrub])

  const seek = useCallback((amount: number) => {
    const element = videoRef.current
    if (!element) return
    requestSeek(element.currentTime + amount)
  }, [requestSeek])

  useEffect(() => {
    let active = true
    const token = prepareToken.current + 1
    prepareToken.current = token
    const sourceKey = streamRemoteKey(stream)
    const identity = `${playbackSessionKey}:${meta.type}:${meta.id}:${video?.id || ''}`
    if (mediaIdentity.current !== identity) {
      mediaIdentity.current = identity
      desiredPlaying.current = resumePlaying
      autoplayCancelled.current = false
      audioRestore.current = null
      proactiveRefreshUrl.current = ''
      sourceRefreshAttempts.current = 0
      lastRecoveryAt.current = 0
      lastSaved.current = resumeAt
      maximumDurationSeen.current = 0
    }
    if (recoverySourceKey.current !== sourceKey) {
      recoverySourceKey.current = sourceKey
      sourceRefreshAttempts.current = 0
    }
    sourceRefreshToken.current += 1
    reloadAttempts.current = 0
    recoveryInFlight.current = false
    pendingOfflineRecovery.current = null
    waitingSince.current = 0
    lastProgressAt.current = Date.now()
    lastProgressTime.current = 0
    clearSeekWatchdog()
    window.clearTimeout(videoOutputTimer.current)
    videoProgressSample.current = null
    triedFallback.current = false
    fallbackOrigin.current = null
    restoredProgress.current = false
    automaticSubtitle.current = false
    compatibilityRecorded.current = false
    successfulTransport.current = ''
    audioPreferenceApplied.current = false
    serverAudioProbe.current = ''
    pendingServerAudio.current = null
    pipelineResume.current = null
    setNextCountdown(null)
    setOledSafe(false)
    setPrepared(null)
    setLoading(true)
    setError('')
    setPlaying(false)
    setCurrent(0)
    setDuration(0)
    setBuffered(0)
    setBufferedAhead(0)
    setStats({})
    setSpeedSamples([])
    setMediaReady(false)
    setHealthDeadlineReached(false)
    setStartupBufferDeadlineReached(false)
    setStartupAllowed(false)
    setStartupWarning(null)
    setAudioTracks([])
    setServerAudioTracks([])
    setServerAudioLoading(false)
    setServerAudioError('')
    setMediaAttempt(0)
    setRecoveryMessage('')
    const audio = getStreamAudio(stream)
    const codec = getStreamCodec(stream)
    const audioSupport = audio === 'aac' || audio === 'ac3' || audio === 'eac3' || audio === 'opus'
      ? codecCapabilities[audio] : undefined
    const codecSupport = codec === 'h264' || codec === 'hevc' || codec === 'vp9' || codec === 'av1'
      ? codecCapabilities[codec] : undefined
    const preferServerCompatibility = codecCapabilities.hls !== 'unsupported'
      && (['dts', 'truehd', 'flac'].includes(audio) || audioSupport === 'unsupported' || codecSupport === 'unsupported')
    const needsServer = Boolean(stream.infoHash || (stream.behaviorHints?.notWebReady && !stream.behaviorHints.proxyHeaders))
    const learnedTransport = preferredTransport(
      stream,
      preferServerCompatibility || (needsServer && settings.preferHls),
      getCompatibilityEvidence(),
    )
    if (preferServerCompatibility && learnedTransport === 'hls') setRecoveryMessage('Using server format compatibility path…')
    prepareStream(stream, video, settings, { transport: learnedTransport })
      .then((value) => active && prepareToken.current === token && setPrepared(value))
      .catch((reason) => {
        if (!active || prepareToken.current !== token) return
        recordCompatibilityOutcome(streamCompatibilityKey(stream), 'failure', reason instanceof Error ? reason.message : 'prepare failed')
        compatibilityRecorded.current = true
        setError(reason instanceof Error ? reason.message : 'Could not prepare this source.')
        setLoading(false)
      })
    return () => { active = false }
    // Subtitle preferences must not restart the movie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSeekWatchdog, codecCapabilities.hls, playbackSessionKey, prepareAttempt, settings.preferHls, settings.serverUrl, stream, video])

  useEffect(() => {
    const probeUrl = prepared?.probeUrl
    if (!mediaReady || !startupAllowed || !probeUrl || !prepared?.audioHlsUrl || serverAudioProbe.current === probeUrl) return
    // Native multi-audio already satisfies playback and phone control. Probe
    // the server only when LG hides tracks, or when the viewer explicitly
    // opens Audio to inspect the fallback path.
    if (audioTracks.length >= 2 && menu !== 'audio') return
    let active = true
    // ffprobe can briefly compete with the first media reads on a small
    // Stremio server. Let native playback establish a cushion before asking
    // for hidden multi-audio metadata.
    const timer = window.setTimeout(() => {
      if (!active || serverAudioProbe.current === probeUrl) return
      serverAudioProbe.current = probeUrl
      setServerAudioLoading(true)
      setServerAudioError('')
      getServerAudioTracks(settings.serverUrl, probeUrl)
        .then((tracks) => { if (active) setServerAudioTracks(tracks) })
        .catch((reason) => {
          if (active) setServerAudioError(reason instanceof Error ? reason.message : 'Server audio probe unavailable.')
        })
        .finally(() => { if (active) setServerAudioLoading(false) })
    }, menu === 'audio' ? 250 : 2_000)
    return () => { active = false; clearTimeout(timer) }
  }, [audioTracks.length, mediaReady, menu, prepared?.audioHlsUrl, prepared?.probeUrl, settings.serverUrl, startupAllowed])

  useEffect(() => {
    let active = true
    subtitleToken.current += 1
    if (subtitleUrlRef.current) URL.revokeObjectURL(subtitleUrlRef.current)
    subtitleUrlRef.current = ''
    setSubtitles([])
    setSubtitleError('')
    // switched off by the owner: do not even ask the add-on
    if (!config.features.subtitles) { setSubtitleLoading(false); return }
    setSubtitleLoading(true)
    setActiveSubtitle(null)
    setSubtitleUrl('')
    automaticSubtitle.current = false
    getSubtitlesFromAddons(settings, meta.type, video?.id || meta.id)
      .then((value) => active && setSubtitles(value))
      .catch((reason) => active && setSubtitleError(reason instanceof Error ? reason.message : 'Could not load subtitles.'))
      .finally(() => active && setSubtitleLoading(false))
    return () => { active = false }
  // Narrowed on purpose: only the provider list and the title decide whether to
  // re-query. Depending on all of `settings` would drop the active subtitle
  // every time an unrelated preference is saved.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.features.subtitles, meta.id, meta.type, settings.addons, settings.subtitlesUrl, video?.id])

  const chooseSubtitle = useCallback(async (choice: Subtitle | null, persist = true) => {
    const token = subtitleToken.current + 1
    subtitleToken.current = token
    if (!choice) {
      if (subtitleUrlRef.current) URL.revokeObjectURL(subtitleUrlRef.current)
      subtitleUrlRef.current = ''
      setSubtitleUrl('')
      setActiveSubtitle(null)
      if (persist) onSettingsChange({ ...settings, subtitlesEnabled: false })
      return
    }
    setSubtitleLoading(true)
    setSubtitleError('')
    try {
      const url = await subtitleBlobUrl(choice.url, settings.subtitleDelay, settings.subtitleEncoding, settings.subtitlePosition)
      if (subtitleToken.current !== token) { URL.revokeObjectURL(url); return }
      if (subtitleUrlRef.current) URL.revokeObjectURL(subtitleUrlRef.current)
      subtitleUrlRef.current = url
      setSubtitleUrl(url)
      setActiveSubtitle(choice)
      if (persist) onSettingsChange({ ...settings, subtitlesEnabled: true, subtitleLanguage: choice.lang })
    } catch (reason) {
      if (subtitleToken.current === token) setSubtitleError(reason instanceof Error ? reason.message : 'Could not load this subtitle.')
    } finally {
      if (subtitleToken.current === token) setSubtitleLoading(false)
    }
  }, [onSettingsChange, settings])

  useEffect(() => {
    if (!activeSubtitle) return
    chooseSubtitle(activeSubtitle, false)
    // Rebuild the WebVTT blob only when the timing/decoder preference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.subtitleDelay, settings.subtitleEncoding, settings.subtitlePosition])

  useEffect(() => {
    if (!settings.subtitlesEnabled || !subtitleChoices.length || automaticSubtitle.current || activeSubtitle) return
    automaticSubtitle.current = true
    const preferred = subtitleChoices.find((item) => languageMatches(item.lang, settings.subtitleLanguage)) || subtitleChoices[0]
    chooseSubtitle(preferred, false)
  }, [activeSubtitle, chooseSubtitle, settings.subtitleLanguage, settings.subtitlesEnabled, subtitleChoices])

  useEffect(() => () => {
    subtitleToken.current += 1
    if (subtitleUrlRef.current) URL.revokeObjectURL(subtitleUrlRef.current)
  }, [])

  useEffect(() => {
    if (!subtitleUrl) return
    const timer = window.setTimeout(() => {
      const tracks = videoRef.current?.textTracks
      if (!tracks) return
      for (let index = 0; index < tracks.length; index += 1) tracks[index].mode = index === tracks.length - 1 ? 'showing' : 'disabled'
    }, 100)
    return () => clearTimeout(timer)
  }, [mediaAttempt, prepared?.url, subtitleUrl])

  useEffect(() => {
    if (!prepared?.statsUrl) return
    let active = true
    let timer = 0
    const load = async () => {
      try {
        const value = await getEngineStats(prepared.statsUrl!)
        if (!active) return
        setStats(value || {})
        if (typeof value.downloadSpeed === 'number') {
          setSpeedSamples((current) => [...current, Math.max(0, value.downloadSpeed || 0)].slice(-5))
        }
      } catch { /* a media stall watchdog owns user-visible recovery */ }
      finally {
        // Never overlap slow 5-second stats requests. Concurrent polling can
        // steal the exact server/network headroom the movie needs.
        if (active) timer = window.setTimeout(load, 1_200)
      }
    }
    void load()
    return () => { active = false; clearTimeout(timer) }
  }, [prepared?.statsUrl])

  useEffect(() => {
    if (!prepared || mediaReady || error) return
    // Never leave the viewer on an endless spinner when webOS accepts a URL
    // but emits neither canplay nor an error. Torrent first-byte discovery gets
    // more time than direct media; HLS gets room for manifest/segment startup.
    const delay = prepared.infoHash ? 60_000 : prepared.transport === 'hls' ? 35_000 : 25_000
    const timer = window.setTimeout(() => {
      const element = videoRef.current
      if (!element || element.readyState >= 3 || recoveryInFlight.current) return
      waitingSince.current = Date.now() - delay
      recoveryHandler.current('startup-timeout')
    }, delay)
    return () => clearTimeout(timer)
  }, [error, mediaAttempt, mediaReady, prepared])

  useEffect(() => {
    if (!mediaReady || startupAllowed || startupWarning || !prepared?.infoHash) return
    const timer = window.setTimeout(() => setHealthDeadlineReached(true), 4500)
    return () => clearTimeout(timer)
  }, [mediaReady, prepared?.infoHash, startupAllowed, startupWarning])

  useEffect(() => {
    if (!mediaReady || startupAllowed || startupWarning || !prepared?.infoHash) return
    // webOS sometimes refuses to extend buffered ranges until play(). Keep the
    // adaptive cushion, but never deadlock startup on that native quirk.
    const timer = window.setTimeout(() => setStartupBufferDeadlineReached(true), 18_000)
    return () => clearTimeout(timer)
  }, [mediaReady, prepared?.infoHash, startupAllowed, startupWarning])

  useEffect(() => {
    if (!mediaReady || !prepared || startupAllowed || startupWarning) return
    if (!prepared.infoHash) { startPreparedPlayback(); return }

    const health = assessStartupHealth({
      streamLength: stats.streamLen || getStreamSize(stream),
      duration: Math.max(Number.isFinite(duration) ? duration : 0, parseRuntimeSeconds(meta.runtime)),
      downloadSpeed: stableDownloadSpeed(speedSamples),
      bufferedSeconds: bufferedAhead,
      peers: stats.peers,
      streamProgress: stats.streamProgress,
    })
    const positiveSamples = speedSamples.filter((value) => value > 0).length
    const enoughEvidence = (stats.streamProgress !== undefined && stats.streamProgress >= 0.98)
      || positiveSamples >= 2 || speedSamples.length >= 3 || healthDeadlineReached
    if (!enoughEvidence) return

    if (health.status === 'warning' && config.features.startupHealthCheck) {
      videoRef.current?.pause()
      setStartupWarning(health)
      setLoading(false)
      showControls()
      return
    }
    const target = startupBufferTarget(health.requiredSpeed, health.downloadSpeed, prepared.transport)
    const fullyAvailable = (stats.streamProgress || 0) >= 0.98
    if (!fullyAvailable && bufferedAhead + 0.5 < target && !startupBufferDeadlineReached) return
    startPreparedPlayback()
  }, [bufferedAhead, config.features.startupHealthCheck, duration, healthDeadlineReached, mediaReady, meta.runtime, prepared, showControls, speedSamples, startPreparedPlayback, startupAllowed, startupBufferDeadlineReached, startupWarning, stats, stream])

  useEffect(() => {
    if (!startupWarning) return
    const timer = window.setTimeout(() => applyFocus(document.querySelector<HTMLElement>('.player-warning [data-autofocus]')), 80)
    return () => clearTimeout(timer)
  }, [startupWarning])

  useEffect(() => {
    if (!playing) return
    const observed = stableDownloadSpeed(speedSamples)
    if (observed && speedSamples.filter((value) => value > 0).length >= 3) saveMeasuredSpeed(observed)
  }, [playing, speedSamples])

  useEffect(() => {
    if (!playing || current < 8) return
    if (!compatibilityRecorded.current) {
      compatibilityRecorded.current = true
      recordCompatibilityOutcome(streamCompatibilityKey(stream), 'success')
    }
    if (prepared?.transport && successfulTransport.current !== prepared.transport) {
      successfulTransport.current = prepared.transport
      recordCompatibilityOutcome(streamTransportCompatibilityKey(stream, prepared.transport), 'success')
    }
  }, [current, playing, prepared?.transport, stream])

  useEffect(() => {
    if (!sleepDeadline) { setSleepRemaining(0); return }
    const update = () => {
      const remaining = Math.max(0, Math.ceil((sleepDeadline - Date.now()) / 1000))
      setSleepRemaining(remaining)
      if (!remaining) {
        videoRef.current?.pause()
        closePlayer()
      }
    }
    update()
    const timer = window.setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [closePlayer, sleepDeadline])

  const armOledProtection = useCallback(() => {
    window.clearTimeout(oledTimer.current)
    setOledSafe(false)
    if (!settings.oledProtection || playing || loading || Boolean(error)) return
    // Measure actual inactivity, not merely time since Pause. Any controller,
    // pointer or menu action restarts this window.
    oledTimer.current = window.setTimeout(() => {
      chromeVisible.current = false
      setControls(false)
      setMenu(null)
      setDiagnosticImage('')
      setFeedback('')
      window.clearTimeout(commitTimer.current)
      scrubTo.current = null
      setScrubPreview(null)
      setOledSafe(true)
    }, 90_000)
  }, [error, loading, playing, settings.oledProtection])

  useEffect(() => {
    armOledProtection()
    const activity = () => armOledProtection()
    const events = [
      'keydown', 'pointerdown', 'pointermove', 'mousemove', 'wheel',
      'vela:player-command', 'vela:player-open-menu', 'vela:player-show-controls',
    ]
    events.forEach((name) => window.addEventListener(name, activity, true))
    return () => {
      events.forEach((name) => window.removeEventListener(name, activity, true))
      window.clearTimeout(oledTimer.current)
    }
  }, [armOledProtection])

  useEffect(() => {
    if (!settings.autoplayNext || !nextVideo || autoplayCancelled.current || !duration || !playing) {
      setNextCountdown(null)
      return
    }
    const remaining = duration - current
    setNextCountdown(remaining > 0 && remaining <= 15 ? Math.ceil(remaining) : null)
  }, [current, duration, nextVideo, playing, settings.autoplayNext])

  useEffect(() => {
    const handleKeys = (event: KeyboardEvent) => {
      const code = event.keyCode || event.which
      const isBack = code === 461 || event.key === 'Escape'
      const stop = () => { event.preventDefault(); event.stopImmediatePropagation() }

      if (isBack) {
        stop()
        if (diagnosticImage) setDiagnosticImage('')
        else if (menu) closeMenu()
        else if (scrubTo.current !== null) cancelScrub()
        else closePlayer()
        return
      }

      const onScrubber = document.activeElement?.classList.contains('timeline-track')
      if (code === 403) { stop(); showFeedback('Sources'); openMenu('sources'); return }
      if (code === 404) { stop(); if (config.features.subtitles) { showFeedback('Subtitles'); openMenu('subtitles') } return }
      if (code === 405) { stop(); showFeedback('Audio'); openMenu('audio'); return }
      if (code === 406) { stop(); showFeedback('Playback diagnostics'); openMenu('stats'); return }

      const digit = code >= 48 && code <= 57 ? code - 48 : code >= 96 && code <= 105 ? code - 96 : -1
      if (digit >= 0) {
        stop()
        if (menu === 'episodes' && digit > 0 && videos[digit - 1]) {
          showFeedback(`Episode ${digit}`)
          if (videos[digit - 1].id !== video?.id) onPlayEpisode(videos[digit - 1])
        } else {
          const element = videoRef.current
          if (element?.duration) {
            const percent = digit * 10
            requestSeek(element.duration * (percent / 100))
            showFeedback(`${percent}%`)
          }
        }
        return
      }

      // Dedicated LG transport keys are explicit commands and must work even
      // while chrome is hidden. The reveal-only rule applies to navigation.
      if (code === 412 || code === 417) {
        stop()
        const now = Date.now()
        const direction = code === 412 ? -1 : 1
        const held = hardwareSeek.current.direction === direction
          && (event.repeat || now - hardwareSeek.current.at < 450)
        hardwareSeek.current = {
          at: now,
          step: held ? Math.min(300, hardwareSeek.current.step * 2) : 30,
          direction,
        }
        const amount = direction * hardwareSeek.current.step
        seek(amount)
        showFeedback(`${amount > 0 ? '+' : '−'}${Math.abs(amount)} sec`)
        return
      }
      if (code === 413) { stop(); showFeedback('Stopped'); closePlayer(); return }
      if (code === 415) { stop(); requestPlay(); showFeedback('Play'); showControls(); return }
      if (code === 19) { stop(); requestPause(); showFeedback('Pause'); showControls(); return }
      if (event.code === 'Space') { stop(); togglePlay(); return }

      const revealed = showControls()
      if (revealed) { stop(); return }

      if (onScrubber && (code === 37 || code === 39)) { stop(); scrub(code === 37 ? -1 : 1); return }
      if (onScrubber && (code === 13 || event.key === 'Enter') && scrubTo.current !== null) { stop(); commitScrub() }
    }
    window.addEventListener('keydown', handleKeys, true)
    return () => window.removeEventListener('keydown', handleKeys, true)
  }, [cancelScrub, chooseAnotherSource, closeMenu, closePlayer, commitScrub, config.features.subtitles, diagnosticImage, menu, onPlayEpisode, openMenu, requestPause, requestPlay, requestSeek, scrub, seek, showControls, showFeedback, togglePlay, video?.id, videos])

  // Phone BACK is a semantic app event rather than a synthetic key. Claim it
  // before App so it follows the same nested stack as the physical LG button:
  // menu -> scrub preview -> player.
  useEffect(() => {
    const back = (event: Event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      if (diagnosticImage) setDiagnosticImage('')
      else if (menu) closeMenu()
      else if (scrubTo.current !== null) cancelScrub()
      else closePlayer()
    }
    window.addEventListener('vela:back', back, true)
    return () => window.removeEventListener('vela:back', back, true)
  }, [cancelScrub, closeMenu, closePlayer, diagnosticImage, menu])

  useEffect(() => () => {
    window.clearTimeout(hideTimer.current)
    window.clearTimeout(oledTimer.current)
    window.clearTimeout(commitTimer.current)
    window.clearTimeout(feedbackTimer.current)
    window.clearTimeout(seekTimer.current)
    window.clearTimeout(videoOutputTimer.current)
    window.clearTimeout(remoteStateTimer.current)
    sourceRefreshToken.current += 1
    prepareToken.current += 1
    persistProgress()
  }, [persistProgress])

  useEffect(() => {
    const lifecycle = () => {
      const element = videoRef.current
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now()
        resumeWhenVisible.current = Boolean(element && desiredPlaying.current && startupAllowed && !element.ended)
        element?.pause()
        persistProgress()
        return
      }

      const suspendedFor = hiddenAt.current ? Date.now() - hiddenAt.current : 0
      hiddenAt.current = 0
      const shouldResume = resumeWhenVisible.current
        || Boolean(desiredPlaying.current && element && startupAllowed && element.paused && !element.ended)
      resumeWhenVisible.current = false
      if (!shouldResume || !desiredPlaying.current || !element || !startupAllowed) return

      if (prepared && shouldRebuildAfterSuspend(suspendedFor, shouldResume, desiredPlaying.current)) {
        if (recoveryInFlight.current) return
        recoveryInFlight.current = true
        const position = Math.max(0, Number.isFinite(element.currentTime) ? element.currentTime : timelineRef.current.current)
        beginPipelineRecovery(position, 'Reconnecting after TV wake…')
        setMediaAttempt((value) => value + 1)
        setPrepared({ ...prepared })
        releaseRecoveryAfterCommit()
      } else {
        playElement(element)
      }
    }
    document.addEventListener('visibilitychange', lifecycle)
    window.addEventListener('pagehide', persistProgress)
    return () => {
      document.removeEventListener('visibilitychange', lifecycle)
      window.removeEventListener('pagehide', persistProgress)
    }
  // beginPipelineRecovery is redeclared every render; depending on it would
  // rebind the lifecycle listeners continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistProgress, playElement, prepared, startupAllowed])

  function updateTimeline() {
    const element = videoRef.current
    if (!element) return
    const mediaTime = element.currentTime || 0
    const reportedDuration = element.duration
    const mediaDuration = Number.isFinite(reportedDuration) && reportedDuration > 0
      ? reportedDuration : timelineRef.current.duration
    if (mediaDuration > maximumDurationSeen.current) maximumDurationSeen.current = mediaDuration
    setCurrent(mediaTime)
    setDuration(mediaDuration)
    if (Math.abs(mediaTime - lastProgressTime.current) > 0.08) {
      lastProgressTime.current = mediaTime
      lastProgressAt.current = Date.now()
      waitingSince.current = 0
      if (startupAllowed && desiredPlaying.current && !element.paused && !element.ended) {
        // Some LG pipelines emit waiting/stalled while the clock keeps moving,
        // then omit a matching playing event. Do not leave a false buffering
        // curtain over healthy video.
        setLoading(false)
        setPlaying(true)
        setRecoveryMessage('')
      }
      if (lastRecoveryAt.current && Date.now() - lastRecoveryAt.current >= 90_000) {
        // A long healthy window starts a fresh bounded incident. This permits
        // recovery later in a feature film without rapid direct/HLS flapping.
        triedFallback.current = false
        fallbackOrigin.current = null
        sourceRefreshAttempts.current = 0
        reloadAttempts.current = 0
        lastRecoveryAt.current = 0
      }
      if (fallbackOrigin.current && Date.now() - fallbackOrigin.current.switchedAt >= 30_000) fallbackOrigin.current = null
    }
    if (seekTarget.current !== null && !element.seeking && Math.abs(mediaTime - seekTarget.current) <= 2) clearSeekWatchdog()
    let mediaRanges: TimeRanges | undefined
    try { mediaRanges = element.buffered } catch { mediaRanges = undefined }
    const bufferState = bufferedMediaState(mediaTime, mediaDuration, mediaRanges)
    setBuffered(bufferState.fraction)
    setBufferedAhead(bufferState.ahead)
    if (Math.abs(element.currentTime - lastSaved.current) >= 10) {
      lastSaved.current = element.currentTime
      onProgress(element.currentTime, mediaDuration, desiredPlaying.current)
    }
  }

  function refreshAudioTracks() {
    const next = readAudioTracks(videoRef.current)
    setAudioTracks((currentTracks) => {
      const unchanged = currentTracks.length === next.length && currentTracks.every((track, index) => (
        track.id === next[index]?.id && track.label === next[index]?.label
          && track.language === next[index]?.language && track.enabled === next[index]?.enabled
      ))
      return unchanged ? currentTracks : next
    })
  }

  // Some webOS media pipelines expose MP4/MKV audio tracks shortly after
  // loadedmetadata rather than during it. Poll the tiny native list so the
  // picker and phone state cannot miss late-arriving tracks.
  useEffect(() => {
    if (!prepared) return
    refreshAudioTracks()
    const timer = window.setInterval(refreshAudioTracks, 1000)
    return () => clearInterval(timer)
  // Restart the poll when the media URL changes, not when its wrapper object is
  // replaced by an unrelated state update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepared?.url])

  function ready(target = videoRef.current) {
    const element = target
    if (!element || element !== videoRef.current) return
    refreshAudioTracks()
    if (pipelineResume.current !== null && element.duration) {
      const target = Math.max(0, Math.min(pipelineResume.current, Math.max(0, element.duration - 0.5)))
      pipelineResume.current = null
      if (target > 0.25 && Math.abs(element.currentTime - target) > 0.5) {
        requestSeek(target, false)
        setCurrent(target)
        return
      }
    }
    if (!restoredProgress.current && resumeAt > 0.5 && resumeAt < element.duration - 0.5) {
      restoredProgress.current = true
      if (Math.abs(element.currentTime - resumeAt) > 0.5) {
        requestSeek(resumeAt, false)
        setCurrent(resumeAt)
        return
      }
    }
    if (element.readyState < 3) return
    updateTimeline()
    recoveryInFlight.current = false
    setMediaReady(true)
    setError('')
    showControls()
  }

  function chooseAudio(index: number, remember = true) {
    try {
      const list = (videoRef.current as HTMLVideoElement & { audioTracks?: AudioTrackListLike } | null)?.audioTracks
      const length = Math.max(0, Math.min(24, list?.length || 0))
      if (!list || index < 0 || index >= length) return
      const language = list[index]?.language || ''
      for (let position = 0; position < length; position += 1) {
        if (list[position]) list[position].enabled = position === index
      }
      if (remember) audioRestore.current = { index, language }
      refreshAudioTracks()
    } catch {
      showFeedback('Audio track unavailable')
    }
  }

  function activateServerAudio(index: number) {
    if (!prepared?.audioHlsUrl || !serverAudioTracks[index]) return
    audioRestore.current = { index, language: serverAudioTracks[index].language || '' }
    if (prepared.url === prepared.audioHlsUrl && audioTracks.length > index) {
      chooseAudio(index)
      showFeedback(`${languageName(serverAudioTracks[index].language)}`)
      return
    }
    const element = videoRef.current
    pipelineResume.current = Math.max(0, element?.currentTime || current)
    pendingServerAudio.current = index
    restoredProgress.current = true
    audioPreferenceApplied.current = true
    triedFallback.current = false
    fallbackOrigin.current = null
    setMenu(null)
    setLoading(true)
    setPlaying(false)
    setMediaReady(false)
    setHealthDeadlineReached(false)
    setStartupAllowed(false)
    setStartupWarning(null)
    setError('')
    setPrepared({ ...prepared, url: prepared.audioHlsUrl, transport: 'hls' })
    showFeedback(`Server audio · ${languageName(serverAudioTracks[index].language)}`)
  }

  useEffect(() => {
    const requested = pendingServerAudio.current
    if (requested === null || audioTracks.length < 2) return
    const serverTrack = serverAudioTracks[requested]
    const language = serverTrack?.language
    const matching = language ? audioTracks.find((track) => languageMatches(track.language, language)) : undefined
    pendingServerAudio.current = null
    chooseAudio(matching?.index ?? Math.min(requested, audioTracks.length - 1))
  // chooseAudio is a plain declaration, new on every render. This must run when
  // the track lists change, not on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioTracks, serverAudioTracks])

  useEffect(() => {
    const requested = audioRestore.current
    if (!requested || audioTracks.length < 2) return
    const selected = audioTracks.find((track) => track.enabled)
    const target = requested.language
      ? audioTracks.find((track) => languageMatches(track.language, requested.language))
      : audioTracks[Math.min(requested.index, audioTracks.length - 1)]
    if (!target || selected?.index === target.index) return
    audioPreferenceApplied.current = true
    chooseAudio(target.index, false)
  // Same: reacting to the track list, not to chooseAudio's identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioTracks])

  useEffect(() => {
    audioPreferenceApplied.current = false
    audioRestore.current = null
  }, [settings.preferredAudioLanguage])

  useEffect(() => {
    if (audioPreferenceApplied.current || audioTracks.length < 2) return
    audioPreferenceApplied.current = true
    const preferred = settings.preferredAudioLanguage
    const target = audioTracks.find((track) => languageMatches(track.language, preferred))
    if (target) chooseAudio(target.index)
  // Same: reacting to the track list and the stated preference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioTracks, settings.preferredAudioLanguage])

  useEffect(() => {
    if (audioPreferenceApplied.current || !mediaReady || serverAudioLoading
      || serverAudioTracks.length < 2 || !prepared?.audioHlsUrl
      || prepared.url === prepared.audioHlsUrl || audioTracks.length >= 2) return
    const preferred = settings.preferredAudioLanguage
    const requested = serverAudioTracks.findIndex((track) => languageMatches(track.language, preferred))
    if (requested < 0) return
    // The direct/default pipeline already has the preferred language. Only
    // restart through master.m3u8 when LG is hiding a non-default track.
    if (serverAudioTracks[requested].default) {
      audioPreferenceApplied.current = true
      return
    }
    const nativeMatch = audioTracks.find((track) => languageMatches(track.language, preferred))
    if (nativeMatch) {
      audioPreferenceApplied.current = true
      chooseAudio(nativeMatch.index)
      return
    }
    // webOS can publish native audioTracks late. Give it one final polling
    // interval before choosing the server fallback automatically.
    const timer = window.setTimeout(() => {
      if (!audioPreferenceApplied.current) activateServerAudio(requested)
    }, 1_500)
    return () => clearTimeout(timer)
  // chooseAudio/activateServerAudio are redeclared each render; this fallback
  // must be driven by the probe results alone.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioTracks, mediaReady, prepared, serverAudioLoading, serverAudioTracks, settings.preferredAudioLanguage])

  function chooseRate(rate: number) {
    if (videoRef.current) videoRef.current.playbackRate = rate
    setPlaybackRate(rate)
  }

  // Switching to the server multi-audio pipeline replaces the <video>
  // element. Reapply the selected rate after that commit so the control and
  // the actual media engine cannot disagree.
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate
  }, [mediaAttempt, playbackRate, prepared?.url])

  function chooseSleep(value: SleepChoice) {
    setSleep(value)
    setSleepDeadline(typeof value === 'number' && value > 0 ? Date.now() + value * 60_000 : 0)
  }

  useEffect(() => {
    const show = () => { showControls() }
    const menuCommand = (event: Event) => {
      const value = (event as CustomEvent<PlayerMenu>).detail
      if (value === 'sources' || value === 'subtitles' || value === 'audio' || value === 'episodes' || value === 'chapters' || value === 'speed' || value === 'picture' || value === 'sleep' || value === 'stats') openMenu(value)
    }
    const command = (event: Event) => {
      const action = (event as CustomEvent<unknown>).detail
      if (!isRemotePlayerCommand(action)) return
      const element = videoRef.current
      if (action.kind === 'stop') { showFeedback('Stopped'); closePlayer(); return }
      if (action.kind === 'play') {
        requestPlay()
        showFeedback('Play')
        showControls()
        return
      }
      if (action.kind === 'pause') { requestPause(); showFeedback('Pause'); showControls(); return }
      if (action.kind === 'toggle') { showFeedback(element?.paused ? 'Play' : 'Pause'); togglePlay(); return }
      if (action.kind === 'seek_by') { showFeedback(`${action.seconds > 0 ? '+' : '−'}${Math.abs(action.seconds)} sec`); seek(action.seconds); return }
      if (action.kind === 'seek_to') {
        if (element) requestSeek(action.seconds)
        showFeedback(`Jump to ${formatTime(action.seconds)}`)
        showControls()
        return
      }
      if (action.kind === 'subtitle') {
        const choice = action.id === null ? null : subtitleChoices.find((item) => item.id === action.id)
        if (choice || action.id === null) { chooseSubtitle(choice || null); showFeedback(choice ? languageName(choice.lang) : 'Subtitles off') }
        return
      }
      if (action.kind === 'audio') {
        if (audioTracks[action.index]) chooseAudio(action.index)
        else if (serverAudioTracks[action.index]) activateServerAudio(action.index)
        showFeedback(`Audio ${action.index + 1}`)
        return
      }
      if (action.kind === 'speed') { chooseRate(action.rate); showFeedback(`${action.rate}×`); return }
      if (action.kind === 'picture') { setPicture(action.mode); showFeedback(`Picture ${action.mode}`); return }
      if (action.kind === 'sleep') { chooseSleep(action.value); showFeedback(action.value ? `Sleep ${action.value === 'end' ? 'after episode' : `${action.value} min`}` : 'Sleep off'); return }
      if (action.kind === 'next' && nextVideo) { onPlayEpisode(nextVideo); return }
      if (action.kind === 'previous' && currentVideoIndex > 0) onPlayEpisode(videos[currentVideoIndex - 1])
    }
    window.addEventListener('vela:player-command', command)
    window.addEventListener('vela:player-show-controls', show)
    window.addEventListener('vela:player-open-menu', menuCommand)
    return () => {
      window.removeEventListener('vela:player-command', command)
      window.removeEventListener('vela:player-show-controls', show)
      window.removeEventListener('vela:player-open-menu', menuCommand)
    }
  // chooseAudio/activateServerAudio are redeclared each render; rebinding the
  // phone command listeners that often would drop in-flight commands.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioTracks, chooseSubtitle, closePlayer, currentVideoIndex, nextVideo, onPlayEpisode, openMenu, requestPause, requestPlay, requestSeek, seek, serverAudioTracks, showControls, showFeedback, subtitleChoices, togglePlay, videos])

  useEffect(() => () => {
    window.clearTimeout(remoteStateTimer.current)
    pendingRemoteState.current = undefined
    remoteStateCallback.current(undefined)
  }, [])

  function playbackEnded(target = videoRef.current) {
    if (!target || target !== videoRef.current) return
    const endedAt = Number.isFinite(target.currentTime) ? target.currentTime : timelineRef.current.current
    const expectedDuration = Math.max(maximumDurationSeen.current, timelineRef.current.duration)
    if (prematureMediaEnd(endedAt, expectedDuration)) {
      desiredPlaying.current = true
      recoveryHandler.current('premature-end', endedAt)
      return
    }
    desiredPlaying.current = false
    const completedDuration = expectedDuration || duration
    onProgress(completedDuration, completedDuration, false)
    if (sleep === 'end' || autoplayCancelled.current) closePlayer()
    else onEnded()
  }

  function clickTimeline(event: React.MouseEvent<HTMLButtonElement>) {
    // Enter/OK generates a click without pointer coordinates: commit the
    // D-pad preview. Magic Remote pointer clicks seek to the clicked position.
    if (!event.detail) { commitScrub(); return }
    const element = videoRef.current
    if (!element?.duration) return
    const bounds = event.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
    requestSeek(ratio * element.duration)
    cancelScrub()
    showControls()
  }

  function playAnyway() {
    setHealthDeadlineReached(true)
    setStartupBufferDeadlineReached(true)
    startPreparedPlayback()
  }

  function rememberActiveAudio() {
    const active = audioTracks.find((track) => track.enabled)
    if (active) audioRestore.current = { index: active.index, language: active.language }
  }

  function beginPipelineRecovery(position: number, message: string) {
    rememberActiveAudio()
    pipelineResume.current = Math.max(0, position)
    restoredProgress.current = true
    waitingSince.current = 0
    lastProgressAt.current = Date.now()
    lastProgressTime.current = position
    lastRecoveryAt.current = Date.now()
    clearSeekWatchdog()
    window.clearTimeout(videoOutputTimer.current)
    videoProgressSample.current = null
    setRecoveryMessage(message)
    setLoading(true)
    setPlaying(false)
    setMediaReady(false)
    setHealthDeadlineReached(false)
    setStartupBufferDeadlineReached(false)
    setStartupAllowed(false)
    setStartupWarning(null)
    setAudioTracks([])
    setError('')
  }

  async function refreshPlaybackSource(message: string) {
    if (!stream.url) return false
    const token = sourceRefreshToken.current + 1
    sourceRefreshToken.current = token
    const expectedKey = recoverySourceKey.current
    setRecoveryMessage(message)
    let timeout = 0
    try {
      const choices = await Promise.race([
        getStreamsFromAddons(settings, meta.type, video?.id || meta.id),
        new Promise<StremioStream[]>((_, reject) => {
          timeout = window.setTimeout(() => reject(new Error('source refresh timed out')), 12_000)
        }),
      ])
      if (sourceRefreshToken.current !== token || recoverySourceKey.current !== expectedKey) return true
      const metadataMatch = findRefreshedStream(stream, choices)
      if (!metadataMatch) { setRecoveryMessage(''); return false }
      const at = Math.max(0, videoRef.current?.currentTime || timelineRef.current.current)
      beginPipelineRecovery(at, 'Renewed expiring media link…')
      onSwitchSource(metadataMatch, at, desiredPlaying.current)
      return true
    } catch {
      if (sourceRefreshToken.current !== token || recoverySourceKey.current !== expectedKey) return true
      setRecoveryMessage('')
      return false
    } finally {
      window.clearTimeout(timeout)
    }
  }

  function releaseRecoveryAfterCommit() {
    window.setTimeout(() => { recoveryInFlight.current = false }, 0)
  }

  async function recoverPlayback(cause: PlaybackRecoveryCause, requestedTarget?: number) {
    if (recoveryInFlight.current || !prepared) return
    recoveryInFlight.current = true
    const element = videoRef.current
    const position = Math.max(0, requestedTarget ?? element?.currentTime ?? timelineRef.current.current)
    const fallbackUrl = prepared?.transport === 'hls' ? prepared.directUrl : prepared?.hlsUrl
    const fallbackTransport = prepared?.transport === 'hls' ? 'direct' : 'hls'
    const stalledFor = Date.now() - (waitingSince.current || lastProgressAt.current)
    const streamLength = stats.streamLen || getStreamSize(stream)
    const runtimeSeconds = Math.max(timelineRef.current.duration, parseRuntimeSeconds(meta.runtime))
    const requiredSpeed = streamLength && runtimeSeconds ? streamLength / runtimeSeconds : 0
    const observedSpeed = stableDownloadSpeed(speedSamples) ?? stats.downloadSpeed
    const networkStarved = Boolean(
      (cause === 'stall' || cause === 'startup-timeout')
      && prepared.infoHash && (stats.streamProgress || 0) < 0.98
      && requiredSpeed > 0 && observedSpeed !== undefined && observedSpeed < requiredSpeed * 0.75
      && bufferedAhead < 2,
    )
    const action = playbackRecoveryAction({
      cause,
      stalledFor,
      online: typeof navigator === 'undefined' || navigator.onLine !== false,
      hasAlternate: Boolean(fallbackUrl && fallbackUrl !== prepared.url),
      alternateTried: triedFallback.current,
      hasRollback: Boolean(fallbackOrigin.current),
      networkStarved,
      canRefreshSource: Boolean(stream.url),
      refreshAttempts: sourceRefreshAttempts.current,
      reloadAttempts: reloadAttempts.current,
    })

    if (action === 'none') { recoveryInFlight.current = false; return }
    if (action === 'wait-network') {
      pendingOfflineRecovery.current = { cause, target: requestedTarget }
      setRecoveryMessage('Network lost. Playback will recover when connection returns…')
      setLoading(true)
      recoveryInFlight.current = false
      return
    }

    const reason = cause === 'stall' ? 'sustained playback stall'
      : cause === 'seek-timeout' ? 'seek timeout'
        : cause === 'missing-video' ? 'missing or frozen video output'
          : cause === 'degraded-video' ? 'severe sustained frame loss'
            : cause === 'premature-end' ? 'media ended before its known duration'
            : cause === 'network-error' ? 'media network error'
              : cause === 'startup-timeout' ? 'media startup timeout'
                : cause === 'decode-error' ? 'media decode error'
                  : cause === 'source-not-supported' ? 'source format not supported'
                    : 'HTML media playback error'
    const compatibilityFailure = !networkStarved && cause !== 'network-error' && cause !== 'startup-timeout'
    if (prepared.transport && compatibilityFailure) {
      recordCompatibilityOutcome(streamTransportCompatibilityKey(stream, prepared.transport), 'failure', reason)
    }

    if (action === 'fallback' && fallbackUrl) {
      triedFallback.current = true
      if (prepared.transport) fallbackOrigin.current = { url: prepared.url, transport: prepared.transport, switchedAt: Date.now() }
      beginPipelineRecovery(position, `Recovering through ${fallbackTransport.toUpperCase()}…`)
      setPrepared({ ...prepared, url: fallbackUrl, transport: fallbackTransport })
      releaseRecoveryAfterCommit()
      return
    }

    if (action === 'rollback' && fallbackOrigin.current) {
      const origin = fallbackOrigin.current
      fallbackOrigin.current = null
      beginPipelineRecovery(position, `Rolling back to ${origin.transport.toUpperCase()}…`)
      setPrepared({ ...prepared, url: origin.url, transport: origin.transport })
      releaseRecoveryAfterCommit()
      return
    }

    if (action === 'refresh-source') {
      sourceRefreshAttempts.current += 1
      const refreshed = await refreshPlaybackSource('Refreshing the media link…')
      if (refreshed) releaseRecoveryAfterCommit()
      else {
        recoveryInFlight.current = false
        recoveryHandler.current(cause, requestedTarget)
      }
      return
    }

    if (action === 'reload') {
      reloadAttempts.current += 1
      beginPipelineRecovery(position, 'Restarting the TV media pipeline…')
      setMediaAttempt((value) => value + 1)
      setPrepared({ ...prepared })
      releaseRecoveryAfterCommit()
      return
    }

    if (!compatibilityRecorded.current && compatibilityFailure) {
      compatibilityRecorded.current = true
      recordCompatibilityOutcome(streamCompatibilityKey(stream), 'failure', reason)
    }
    clearSeekWatchdog()
    pendingOfflineRecovery.current = null
    setRecoveryMessage('')
    setLoading(false)
    setError(cause === 'seek-timeout'
      ? 'The TV could not finish seeking after automatic recovery. Try another source.'
      : cause === 'stall'
        ? 'Playback kept stalling after automatic recovery. Choose a smaller or better-seeded source.'
        : cause === 'startup-timeout'
          ? 'The source never became ready after automatic recovery. Try another source.'
          : cause === 'network-error'
            ? 'The media connection failed after automatic recovery. Check the server or try another source.'
            : cause === 'premature-end'
              ? 'The source ended early after automatic recovery. Try another source.'
            : cause === 'missing-video' || cause === 'degraded-video'
          ? 'The picture stopped or never appeared after automatic recovery. Try another source.'
          : 'This source could not be played after automatic recovery. Try another source.')
    recoveryInFlight.current = false
  }

  recoveryHandler.current = (cause, target) => { void recoverPlayback(cause, target) }

  function playbackError(target = videoRef.current) {
    if (!target || target !== videoRef.current) return
    const cause = classifyMediaError(target.error?.code || 0)
    if (cause) recoveryHandler.current(cause)
  }

  function playbackStarted(target = videoRef.current) {
    const element = target
    if (!element || element !== videoRef.current) return
    if (!desiredPlaying.current || document.visibilityState === 'hidden') {
      element.pause()
      return
    }
    waitingSince.current = 0
    recoveryInFlight.current = false
    lastProgressAt.current = Date.now()
    lastProgressTime.current = element.currentTime || 0
    videoProgressSample.current = null
    setPlaying(true)
    setLoading(false)
    setStartupAllowed(true)
    setRecoveryMessage('')
    window.clearTimeout(videoOutputTimer.current)
    const baseline = element.currentTime || 0
    videoOutputTimer.current = window.setTimeout(() => {
      const currentElement = videoRef.current
      if (currentElement && desiredPlaying.current && currentElement.currentTime > baseline + 1
        && currentElement.videoWidth === 0 && currentElement.videoHeight === 0) {
        recoveryHandler.current('missing-video')
      }
    }, 5_000)
  }

  function playbackPaused(target = videoRef.current) {
    if (!target || target !== videoRef.current) return
    setPlaying(false)
    persistProgress()
  }

  function playbackWaiting(target = videoRef.current) {
    if (!target || target !== videoRef.current) return
    if (!startupAllowed || !desiredPlaying.current) return
    if (!waitingSince.current) waitingSince.current = Date.now()
    setLoading(true)
  }

  function playbackSeeked(target = videoRef.current) {
    const element = target
    if (!element || element !== videoRef.current) return
    if (seekTarget.current !== null && Math.abs(element.currentTime - seekTarget.current) <= 2) clearSeekWatchdog()
    ready(element)
  }

  const retryPlayback = useCallback(() => {
    triedFallback.current = false
    fallbackOrigin.current = null
    sourceRefreshAttempts.current = 0
    reloadAttempts.current = 0
    pendingOfflineRecovery.current = null
    compatibilityRecorded.current = false
    successfulTransport.current = ''
    setError('')
    setLoading(true)
    setMediaReady(false)
    setStartupAllowed(false)
    if (!prepared) { setPrepareAttempt((value) => value + 1); return }
    waitingSince.current = Date.now() - 8_000
    recoveryHandler.current('media-error')
  }, [prepared])

  useEffect(() => {
    const online = () => {
      const pending = pendingOfflineRecovery.current
      if (pending) {
        pendingOfflineRecovery.current = null
        waitingSince.current = Date.now() - 8_000
        recoveryHandler.current(pending.cause, pending.target)
      } else if (error) retryPlayback()
    }
    window.addEventListener('online', online)
    return () => window.removeEventListener('online', online)
  }, [error, retryPlayback])

  useEffect(() => {
    if (!prepared || !startupAllowed) return
    const timer = window.setInterval(() => {
      const element = videoRef.current
      if (!element || !desiredPlaying.current || document.visibilityState === 'hidden' || element.ended || element.seeking) return
      const mediaTime = element.currentTime || 0
      const quality = readVideoFrameCounters(element)
      if (quality && quality.totalVideoFrames > 0) {
        const sample: VideoProgressSample = { observedAt: Date.now(), mediaTime, ...quality }
        const previous = videoProgressSample.current
        if (!previous || sample.observedAt - previous.observedAt >= 6_000) {
          videoProgressSample.current = sample
          if (previous && frozenVideoOutput(previous, sample)) {
            recoveryHandler.current('missing-video')
            return
          }
          if (previous && degradedVideoOutput(previous, sample)) {
            recoveryHandler.current('degraded-video')
            return
          }
        }
      }
      if (Math.abs(mediaTime - lastProgressTime.current) > 0.08) {
        lastProgressTime.current = mediaTime
        lastProgressAt.current = Date.now()
        waitingSince.current = 0
        return
      }
      if (element.duration && mediaTime >= element.duration - 1) return
      const stalledFor = Date.now() - (waitingSince.current || lastProgressAt.current)
      if (stalledFor >= 7_000) recoveryHandler.current('stall')
    }, 1_000)
    return () => clearInterval(timer)
  }, [prepared, startupAllowed])

  useEffect(() => {
    const expiresAt = mediaUrlExpiresAt(stream.url)
    if (!stream.url || !expiresAt || proactiveRefreshUrl.current === stream.url) return
    const delay = Math.max(0, expiresAt - Date.now() - 90_000)
    if (delay > 2_147_000_000) return
    const timer = window.setTimeout(() => {
      if (recoveryInFlight.current) return
      proactiveRefreshUrl.current = stream.url || ''
      recoveryInFlight.current = true
      void refreshPlaybackSource('Renewing media link before it expires…').then((refreshed) => {
        if (refreshed) releaseRecoveryAfterCommit()
        else {
          recoveryInFlight.current = false
          if (videoRef.current?.error || expiresAt <= Date.now()) recoveryHandler.current('media-error')
        }
      })
    }, delay)
    return () => clearTimeout(timer)
    // refreshPlaybackSource intentionally follows the current source identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.url])

  const percent = duration ? current / duration : 0
  const shown = duration && scrubPreview !== null ? scrubPreview / duration : percent
  const streamSize = stats.streamLen || getStreamSize(stream)
  const runtime = Math.max(duration, parseRuntimeSeconds(meta.runtime))
  const requiredSpeed = streamSize && runtime ? streamSize / runtime : 0
  const startupTarget = startupBufferTarget(requiredSpeed, stats.downloadSpeed || 0, prepared?.transport)
  const secondsToTarget = (stats.downloadSpeed || 0) > 0 && requiredSpeed > 0
    ? Math.max(0, Math.ceil((Math.max(0, startupTarget - bufferedAhead) * requiredSpeed) / (stats.downloadSpeed || 1))) : undefined
  const bufferingCopy = recoveryMessage || (stats.peers
    ? `${stats.peers} peers · ${formatBytesPerSecond(stats.downloadSpeed)} · ${Math.round(bufferedAhead)}/${startupTarget} sec ready${secondsToTarget !== undefined ? ` · starting in ~${secondsToTarget} sec` : ''}`
    : 'Connecting to your Stremio server…')
  const videoElement = videoRef.current
  // Only the diagnostics panel shows these, and this runs on every timeupdate.
  const playbackQuality = menu === 'stats' ? readVideoFrameCounters(videoElement) : undefined
  const menuTitle = menu === 'sources' ? 'Switch source'
    : menu === 'subtitles' ? 'Subtitles'
    : menu === 'audio' ? 'Audio track'
      : menu === 'episodes' ? 'Episodes'
        : menu === 'chapters' ? 'Chapters'
          : menu === 'speed' ? 'Playback speed'
            : menu === 'picture' ? 'Picture'
              : menu === 'sleep' ? 'Sleep timer'
                : 'Playback diagnostics'
  const playingSourceListed = sourceChoices.slice(0, sourceVisibleCount)
    .some((choice) => streamRemoteKey(choice.stream) === streamRemoteKey(stream))
  const episodePages = Math.max(1, Math.ceil(videos.length / EPISODE_MENU_PAGE_SIZE))
  const episodePageStart = Math.min(episodePage, episodePages - 1) * EPISODE_MENU_PAGE_SIZE
  const episodeMenuItems = videos.slice(episodePageStart, episodePageStart + EPISODE_MENU_PAGE_SIZE)
  const remoteEpisodeStart = Math.max(0, Math.min(
    Math.max(0, currentVideoIndex) - 45,
    Math.max(0, videos.length - 100),
  ))

  async function buildDiagnosticQr() {
    const quality = readVideoFrameCounters(videoRef.current)
    setDiagnosticImage(await diagnosticQr({
      v: 1,
      createdAt: new Date().toISOString(),
      appVersion: APP_VERSION,
      tvModel: currentTvModel(),
      runtime: typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent,
      media: `${meta.type}:${meta.id}${video?.id ? `:${video.id}` : ''}`,
      source: {
        resolution: getStreamResolution(stream).label,
        codec: getStreamCodec(stream) || 'unknown',
        audio: getStreamAudio(stream) || 'unknown',
        container: getStreamContainer(stream) || 'unknown',
        hdr: streamHdr || 'SDR',
        transport: `${prepared?.infoHash ? 'torrent/' : ''}${prepared?.transport || 'unknown'}`,
      },
      playback: {
        currentTime: current,
        duration,
        bufferedSeconds: bufferedAhead,
        downloadSpeed: stats.downloadSpeed || 0,
        peers: stats.peers || 0,
        droppedFrames: quality?.droppedVideoFrames || 0,
        totalFrames: quality?.totalVideoFrames || 0,
        error: error || undefined,
      },
    }))
  }

  const remoteSubtitles = useMemo(() => [
    { id: 'off', label: 'Off', active: !activeSubtitle },
    ...subtitleChoices.slice(0, 63).map((choice, index) => ({
      id: boundedRemoteText(choice.id, 240, `subtitle-${index + 1}`),
      label: boundedRemoteText(languageName(choice.lang), 200, `Subtitle ${index + 1}`),
      detail: boundedRemoteText(choice.addonName, 160, 'Subtitle add-on'),
      active: activeSubtitle?.id === choice.id,
    })),
  ], [activeSubtitle, subtitleChoices])
  const remoteAudio = useMemo(() => ((audioTracks.length > 1 || serverAudioTracks.length < 2) ? audioTracks.map((track) => ({
      id: String(track.index),
      label: boundedRemoteText(track.label, 200, `Audio ${track.index + 1}`),
      detail: track.language ? boundedRemoteText(languageName(track.language), 160) : undefined,
      active: track.enabled,
    })) : serverAudioTracks.length > 1 ? serverAudioTracks.map((track, index) => ({
      id: String(index),
      label: boundedRemoteText(track.language ? languageName(track.language) : '', 200, `Audio ${index + 1}`),
      detail: boundedRemoteText(`${track.codec.toUpperCase()} · server fallback`, 160),
      active: track.default,
    })) : []).slice(0, 24), [audioTracks, serverAudioTracks])
  const remoteEpisodes = useMemo(() => videos.slice(remoteEpisodeStart, remoteEpisodeStart + 100).map((item, index) => ({
    id: boundedRemoteText(item.id, 240, `episode-${remoteEpisodeStart + index + 1}`),
    label: boundedRemoteText(episodeText(item, `Episode ${remoteEpisodeStart + index + 1}`), 200),
    detail: item.name || item.title ? boundedRemoteText(item.name || item.title, 300) : undefined,
    active: item.id === video?.id,
    season: Number.isInteger(item.season) ? boundedRemoteNumber(item.season, 0, 100_000) : undefined,
    episode: Number.isInteger(item.episode) ? boundedRemoteNumber(item.episode, 0, 100_000) : undefined,
  })), [remoteEpisodeStart, video?.id, videos])

  useEffect(() => {
    const nextState: RemotePlayerState = {
      status: error ? 'error' : loading ? (prepared ? 'buffering' : 'preparing') : playing ? 'playing' : 'paused',
      media: toRemoteMediaSummary(meta),
      videoId: video?.id ? boundedRemoteText(video.id, 240) : undefined,
      title: boundedRemoteText(title, 300, 'Untitled'),
      label: boundedRemoteText(label, 120, 'Video'),
      currentTime: boundedRemoteNumber(current, 0, 86_400),
      duration: boundedRemoteNumber(duration, 0, 86_400),
      buffered: boundedRemoteNumber(buffered, 0, 1),
      playing,
      error: error ? boundedRemoteText(error, 500) : undefined,
      subtitle: activeSubtitle ? boundedRemoteText(languageName(activeSubtitle.lang), 120) : undefined,
      subtitles: remoteSubtitles,
      audio: remoteAudio,
      episodes: remoteEpisodes,
      speed: [0.75, 1, 1.25, 1.5].includes(playbackRate) ? playbackRate : 1,
      picture,
      sleep,
      hdr: {
        source: streamHdr,
        capability: streamHdrSupport,
        display: hdrCapabilities.display,
        playing,
        note: boundedRemoteText(hdrStatusNote(streamHdr, hdrCapabilities, playing), 500),
      },
    }
    pendingRemoteState.current = nextState
    const urgent = publishedRemoteStatus.current !== nextState.status
    if (urgent) {
      window.clearTimeout(remoteStateTimer.current)
      remoteStateTimer.current = undefined
      pendingRemoteState.current = undefined
      publishedRemoteStatus.current = nextState.status
      remoteStateCallback.current(nextState)
      return
    }
    if (remoteStateTimer.current) return
    // One phone update per 750 ms keeps controls responsive without forcing the
    // full catalog/detail tree to reconcile on every native timeupdate.
    remoteStateTimer.current = window.setTimeout(() => {
      remoteStateTimer.current = undefined
      const pending = pendingRemoteState.current
      pendingRemoteState.current = undefined
      if (!pending) return
      publishedRemoteStatus.current = pending.status
      remoteStateCallback.current(pending)
    }, 750)
  }, [activeSubtitle, buffered, current, duration, error, hdrCapabilities, label, loading, meta, picture, playbackRate, playing, prepared, remoteAudio, remoteEpisodes, remoteSubtitles, sleep, streamHdr, streamHdrSupport, title, video?.id])

  return (
    <div
      ref={playerRootRef}
      className={`player controls-${controls ? 'visible' : 'hidden'} picture-${picture} subtitles-${settings.subtitleSize} subtitle-position-${settings.subtitlePosition} subtitle-background-${settings.subtitleBackground}${oledSafe ? ' oled-safe' : ''}`}
      onMouseMove={showControls}
    >
      {prepared ? (
        <video
          key={`${prepared.url}:${mediaAttempt}`}
          ref={videoRef}
          src={prepared.url}
          preload="auto"
          playsInline
          onLoadedMetadata={(event) => { if (event.currentTarget === videoRef.current) { updateTimeline(); refreshAudioTracks() } }}
          onCanPlay={(event) => ready(event.currentTarget)}
          onSeeked={(event) => playbackSeeked(event.currentTarget)}
          onPlaying={(event) => playbackStarted(event.currentTarget)}
          onPause={(event) => playbackPaused(event.currentTarget)}
          onWaiting={(event) => playbackWaiting(event.currentTarget)}
          onStalled={(event) => playbackWaiting(event.currentTarget)}
          onTimeUpdate={(event) => { if (event.currentTarget === videoRef.current) updateTimeline() }}
          onProgress={(event) => { if (event.currentTarget === videoRef.current) updateTimeline() }}
          onEnded={(event) => playbackEnded(event.currentTarget)}
          onError={(event) => playbackError(event.currentTarget)}
        >
          {subtitleUrl && activeSubtitle ? <track key={subtitleUrl} kind="subtitles" src={subtitleUrl} srcLang={activeSubtitle.lang} label={languageName(activeSubtitle.lang)} default /> : null}
        </video>
      ) : null}

      {feedback ? <div className="player-feedback" role="status">{feedback}</div> : null}
      {oledSafe ? <div className="oled-pause-mark"><Pause size="1.75rem" fill="currentColor" /> Paused</div> : null}
      {introVisible ? <button className="skip-segment" data-focusable onClick={() => { if (video?.introEnd) requestSeek(video.introEnd); showFeedback('Intro skipped') }}>Skip intro</button> : null}
      {creditsVisible && nextVideo ? <button className="skip-segment credits" data-focusable onClick={() => onPlayEpisode(nextVideo)}>Play {episodeText(nextVideo)}</button> : null}
      {nextCountdown !== null && nextVideo ? (
        <div className="next-countdown">
          <span><small>NEXT EPISODE</small><strong>{episodeText(nextVideo)} in {nextCountdown} sec</strong></span>
          <button data-focusable onClick={() => onPlayEpisode(nextVideo)}>Play now</button>
          <button data-focusable onClick={() => { autoplayCancelled.current = true; setNextCountdown(null); showFeedback('Autoplay cancelled') }}>Cancel</button>
        </div>
      ) : null}

      <div className="player-top chrome-layer">
        <button className="round-button player-back" data-focusable onClick={closePlayer} aria-label="Close player"><ArrowLeft size="2rem" /></button>
        <div className="player-title"><small>{label}</small><strong>{title}</strong></div>
        <div className="player-server"><i /><Server size="1.5rem" /> Streaming from server</div>
      </div>

      {loading && !error ? (
        <div className="player-loader">
          <div className="orbital-loader"><i /><i /><span className="brand-mark"><b /></span></div>
          <h2>{mediaReady && prepared?.infoHash ? 'Checking stream health' : prepared ? 'Buffering your stream' : 'Preparing your stream'}</h2>
          <p>{bufferingCopy}</p>
        </div>
      ) : null}

      {startupWarning && !error ? (
        <div className="player-warning">
          <TriangleAlert size="2.75rem" />
          <h2>Stream may stop again</h2>
          <p>A chunk is ready, but this torrent is not downloading fast enough for smooth playback.</p>
          <div className="player-warning-stats">
            <span>Current <strong>{formatBytesPerSecond(startupWarning.downloadSpeed)}</strong></span>
            <span>Needed <strong>{formatBytesPerSecond(startupWarning.requiredSpeed)}</strong></span>
            <span>Ready <strong>{Math.round(startupWarning.bufferedSeconds)} sec</strong></span>
            {startupWarning.stallInSeconds !== undefined ? <span>Likely pause <strong>{formatTime(startupWarning.stallInSeconds)}</strong></span> : null}
          </div>
          <div className="player-warning-actions">
            <button className="primary-button" data-focusable data-autofocus onClick={chooseAnotherSource}>Choose another source</button>
            <button className="secondary-button" data-focusable onClick={playAnyway}>Play anyway</button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="player-error">
          <X size="2.75rem" /><h2>Playback unavailable</h2><p>{error}</p>
          <div className="retry-actions">
            <button className="primary-button" data-focusable data-autofocus onClick={retryPlayback}><RefreshCw size="1.5rem" /> Retry stream</button>
            <button className="secondary-button" data-focusable onClick={chooseAnotherSource}>Choose another source</button>
            <button className="secondary-button" data-focusable onClick={closePlayer}>Back</button>
          </div>
        </div>
      ) : null}

      <div className="player-controls chrome-layer">
        <div className="stream-health">
          {prepared?.infoHash ? <><Gauge size="1.5rem" /><span>{stats.peers || 0} peers</span><span>{formatBytesPerSecond(stats.downloadSpeed)}</span><span>{Math.round(bufferedAhead)} sec ready</span></> : <><i /><span>Direct stream</span></>}
          {streamHdr ? <span className={`hdr-status ${streamHdrSupport}`} title={hdrStatusNote(streamHdr, hdrCapabilities, playing)}><Sparkles size="1.5rem" /> {streamHdr} · {streamHdrSupport === 'supported' ? 'HDR path ready' : streamHdrSupport === 'unsupported' ? 'fallback advised' : 'verify on TV'}</span> : <span className="hdr-status sdr">SDR</span>}
          {activeSubtitle ? <span><Captions size="1.5rem" /> {languageName(activeSubtitle.lang)}</span> : null}
          {sleepRemaining ? <span><Clock3 size="1.5rem" /> {formatTime(sleepRemaining)}</span> : sleep === 'end' ? <span><Clock3 size="1.5rem" /> Sleep after episode</span> : null}
        </div>
        <div className="timeline">
          <span>{formatTime(scrubPreview ?? current)}</span>
          <button
            className="timeline-track"
            data-focusable
            data-nav-capture="left right"
            data-nav-group="row"
            data-nav-key="scrubber"
            aria-label="Seek"
            onClick={clickTimeline}
            onWheel={(event) => { event.preventDefault(); scrub(event.deltaY > 0 || event.deltaX > 0 ? 1 : -1) }}
          >
            <span className="timeline-rail">
              <i className="buffered" style={{ width: `${buffered * 100}%` }} />
              <i className="played" style={{ width: `${shown * 100}%` }} />
              <b style={{ left: `${shown * 100}%` }} />
              {scrubPreview !== null ? <span className="timeline-bubble" style={{ left: `${shown * 100}%` }}>{formatTime(scrubPreview)}</span> : null}
            </span>
          </button>
          <span>{formatTime(duration)}</span>
        </div>
        <div className="transport" data-nav-group="row" data-nav-key="transport">
          <button className="transport-button transport-wide" data-focusable onClick={() => seek(-config.playback.bigSeekStep)} aria-label={`Back ${config.playback.bigSeekStep} seconds`}><RotateCcw size="1.75rem" /><small>{config.playback.bigSeekStep}</small></button>
          <button className="transport-button" data-focusable onClick={() => seek(-config.playback.seekStep)} aria-label={`Back ${config.playback.seekStep} seconds`}><RotateCcw size="2rem" /><small>{config.playback.seekStep}</small></button>
          <button className="play-button" data-focusable data-autofocus onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>{playing ? <Pause fill="currentColor" size="2.25rem" /> : <Play fill="currentColor" size="2.25rem" />}</button>
          <button className="transport-button" data-focusable onClick={() => seek(config.playback.seekStep)} aria-label={`Forward ${config.playback.seekStep} seconds`}><RotateCw size="2rem" /><small>{config.playback.seekStep}</small></button>
          <button className="transport-button transport-wide" data-focusable onClick={() => seek(config.playback.bigSeekStep)} aria-label={`Forward ${config.playback.bigSeekStep} seconds`}><RotateCw size="1.75rem" /><small>{config.playback.bigSeekStep}</small></button>
          {nextVideo && config.features.nextEpisodeCard ? <button className="transport-button next-button" data-focusable onClick={() => onPlayEpisode(nextVideo)} aria-label={`Play ${episodeText(nextVideo)}`}><SkipForward size="2rem" /><small>Next</small></button> : null}
        </div>
        <div className="player-secondary" data-nav-group="row" data-nav-key="player-secondary">
          <button data-player-menu-button data-player-menu="sources" data-focusable onClick={() => openMenu('sources')}><ListVideo size="1.5rem" /> Sources</button>
          {config.features.subtitles ? <button data-player-menu-button data-player-menu="subtitles" className={activeSubtitle ? 'active' : ''} data-focusable onClick={() => openMenu('subtitles')}><Captions size="1.5rem" /> Subtitles</button> : null}
          {config.features.audioTracks ? <button data-player-menu-button data-player-menu="audio" data-focusable onClick={() => openMenu('audio')}><AudioLines size="1.5rem" /> Audio</button> : null}
          {videos.length ? <button data-player-menu-button data-player-menu="episodes" data-focusable onClick={() => openMenu('episodes')}><ListVideo size="1.5rem" /> Episodes</button> : null}
          {chapters.length ? <button data-player-menu-button data-player-menu="chapters" data-focusable onClick={() => openMenu('chapters')}><ListTree size="1.5rem" /> Chapters</button> : null}
          {config.features.playbackSpeed ? <button data-player-menu-button data-player-menu="speed" data-focusable onClick={() => openMenu('speed')}><GaugeCircle size="1.5rem" /> {playbackRate}×</button> : null}
          {config.features.zoomToggle ? <button data-player-menu-button data-player-menu="picture" data-focusable onClick={() => openMenu('picture')}><Maximize2 size="1.5rem" /> Picture</button> : null}
          {config.features.sleepTimer ? <button data-player-menu-button data-player-menu="sleep" className={sleep ? 'active' : ''} data-focusable onClick={() => openMenu('sleep')}><TimerReset size="1.5rem" /> Sleep</button> : null}
          <button data-player-menu-button data-player-menu="stats" data-focusable onClick={() => openMenu('stats')}><BarChart3 size="1.5rem" /> Stats</button>
        </div>
        <p className="transport-hint">{scrubPreview !== null ? 'Press OK to jump here' : 'Up for timeline · Down for playback options'}</p>
      </div>

      {menu ? (
        <div className="player-menu">
          <div className="player-menu-header">
            <button className="round-button" data-focusable onClick={closeMenu} aria-label="Close player menu"><ArrowLeft size="1.75rem" /></button>
            <div><small>PLAYBACK</small><h2>{menuTitle}</h2></div>
          </div>

          {menu === 'sources' ? (
            <div className="player-menu-list player-source-menu" data-nav-group="col" data-nav-key="player-sources">
              {sourceLoading ? <p>Finding every source…</p> : null}
              {sourceError ? <p className="menu-error">{sourceError}</p> : null}
              {sourceChoices.slice(0, sourceVisibleCount).map((choice, index) => {
                const active = streamRemoteKey(choice.stream) === streamRemoteKey(stream)
                // Focus the source that is playing; only fall back to the top of
                // the list when it is not in view.
                const focusHere = active || (!playingSourceListed && index === 0 && choice.allowed)
                return <button className={active ? 'selected' : choice.policyBlocked ? 'blocked' : ''} disabled={!choice.allowed} data-focusable data-autofocus={focusHere ? '' : undefined} onClick={() => {
                  if (active) return
                  const at = videoRef.current?.currentTime || current
                  persistProgress()
                  setMenu(null)
                  onSwitchSource(choice.stream, at, desiredPlaying.current)
                }} key={`${streamRemoteKey(choice.stream)}:${index}`}>
                  <span><strong>{active ? 'Current source' : index === 0 ? 'Best for this TV' : choice.stream.name || 'Source'}</strong><small>{[choice.resolution.label, choice.codec && choice.codec.toUpperCase(), choice.audio && choice.audio.toUpperCase(), choice.container && choice.container.toUpperCase(), choice.hdr, formatFileSize(choice.size), choice.peers ? `${choice.peers} peers` : '', choice.evidence].filter(Boolean).join(' · ')}</small>{choice.risks.length ? <em>{choice.risks.join(' · ')}</em> : null}</span>{active ? <Check size="1.5rem" /> : choice.allowed ? <Play size="1.5rem" /> : <TriangleAlert size="1.5rem" />}
                </button>
              })}
              {sourceChoices.length > sourceVisibleCount ? <button data-focusable onClick={() => setSourceVisibleCount((value) => Math.min(sourceChoices.length, value + 24))}><span><strong>Show {Math.min(24, sourceChoices.length - sourceVisibleCount)} more sources</strong><small>{sourceChoices.length - sourceVisibleCount} remaining · list stays light for webOS</small></span><ListVideo size="1.5rem" /></button> : null}
              {sourceError ? <button data-focusable data-autofocus onClick={onExitToSources}><span><strong>Open title source list</strong><small>Leave the player only because its in-player list failed.</small></span><ListVideo size="1.5rem" /></button> : null}
            </div>
          ) : null}

          {menu === 'subtitles' ? (
            <div className="player-menu-list" data-nav-group="col" data-nav-key="subtitle-options">
              <button className={!activeSubtitle ? 'selected' : ''} data-focusable data-autofocus onClick={() => chooseSubtitle(null)}><span>Off</span>{!activeSubtitle ? <Check size="1.5rem" /> : null}</button>
              {subtitleLoading && !subtitles.length ? <p>Finding subtitles…</p> : null}
              {subtitleError ? <p className="menu-error">{subtitleError}</p> : null}
              {subtitleChoices.map((choice) => (
                <button className={activeSubtitle?.id === choice.id ? 'selected' : ''} data-focusable onClick={() => chooseSubtitle(choice)} key={choice.id}>
                  <span><strong>{languageName(choice.lang)}</strong><small>{choice.addonName || 'Subtitle add-on'}</small></span>
                  {activeSubtitle?.id === choice.id ? <Check size="1.5rem" /> : null}
                </button>
              ))}
              <div className="menu-option-group" data-nav-group="row" data-nav-key="subtitle-size">
                <span>Text size</span>
                {(['small', 'medium', 'large'] as const).map((size) => <button className={settings.subtitleSize === size ? 'selected' : ''} data-focusable onClick={() => onSettingsChange({ ...settings, subtitleSize: size })} key={size}>{size}</button>)}
              </div>
              <div className="menu-option-group" data-nav-group="row" data-nav-key="subtitle-delay">
                <span>Delay {settings.subtitleDelay > 0 ? '+' : ''}{settings.subtitleDelay.toFixed(1)} sec</span>
                <button data-focusable onClick={() => onSettingsChange({ ...settings, subtitleDelay: Math.max(-30, settings.subtitleDelay - 0.5) })}>−0.5</button>
                <button data-focusable onClick={() => onSettingsChange({ ...settings, subtitleDelay: 0 })}>Reset</button>
                <button data-focusable onClick={() => onSettingsChange({ ...settings, subtitleDelay: Math.min(30, settings.subtitleDelay + 0.5) })}>+0.5</button>
              </div>
              <div className="menu-option-group" data-nav-group="row" data-nav-key="subtitle-position">
                <span>Position</span>
                {(['bottom', 'middle', 'top'] as const).map((value) => <button className={settings.subtitlePosition === value ? 'selected' : ''} data-focusable onClick={() => onSettingsChange({ ...settings, subtitlePosition: value })} key={value}>{value}</button>)}
              </div>
              <div className="menu-option-group" data-nav-group="row" data-nav-key="subtitle-background">
                <span>Background</span>
                {(['none', 'shadow', 'box'] as const).map((value) => <button className={settings.subtitleBackground === value ? 'selected' : ''} data-focusable onClick={() => onSettingsChange({ ...settings, subtitleBackground: value })} key={value}>{value}</button>)}
              </div>
            </div>
          ) : null}

          {menu === 'audio' ? (
            <div className="player-menu-list" data-nav-group="col" data-nav-key="audio-options">
              {!audioTracks.length ? <p>This stream exposes one default audio track. Multi-audio controls appear here when the TV reports them.</p> : audioTracks.map((track, index) => (
                <button className={track.enabled ? 'selected' : ''} data-focusable data-autofocus={track.enabled || index === 0 ? '' : undefined} onClick={() => chooseAudio(track.index)} key={track.id}>
                  <span><strong>{track.label}</strong><small>{track.language ? languageName(track.language) : `Track ${index + 1}`}</small></span>{track.enabled ? <Check size="1.5rem" /> : null}
                </button>
              ))}
              {serverAudioLoading ? <p>Checking the Stremio server for hidden audio tracks…</p> : null}
              {prepared?.audioHlsUrl === prepared?.url ? <p>Server multi-audio fallback is active.</p> : null}
              {prepared?.audioHlsUrl !== prepared?.url && serverAudioTracks.length > 1 ? <>
                <p>LG hiding a track? Restart this source through the server's multi-audio HLS playlist. Playback position is kept.</p>
                {serverAudioTracks.map((track, index) => <button data-server-audio-fallback={index} data-focusable onClick={() => activateServerAudio(index)} key={`server:${track.stream}`}>
                  <span><strong>Play with {track.language ? languageName(track.language) : `Audio ${index + 1}`}</strong><small>{track.codec.toUpperCase()} · server audio fallback</small></span><Server size="1.5rem" />
                </button>)}
              </> : null}
              {serverAudioError && !audioTracks.length ? <p className="menu-error">Server audio fallback unavailable: {serverAudioError}</p> : null}
            </div>
          ) : null}

          {menu === 'episodes' ? (
            <div className="player-menu-list episode-menu-list" data-nav-group="col" data-nav-key="player-episodes">
              {episodePages > 1 ? <p>Episodes {episodePageStart + 1}–{Math.min(videos.length, episodePageStart + EPISODE_MENU_PAGE_SIZE)} of {videos.length}</p> : null}
              {episodeMenuItems.map((item, index) => (
                <button className={item.id === video?.id ? 'selected' : ''} data-focusable data-autofocus={item.id === video?.id ? '' : undefined} onClick={() => item.id !== video?.id && onPlayEpisode(item)} key={item.id}>
                  <span><strong>{episodeText(item)}</strong><small>{item.name || item.title || `Episode ${episodePageStart + index + 1}`}</small></span>{item.id === video?.id ? <Check size="1.5rem" /> : <Play size="1.5rem" />}
                </button>
              ))}
              {episodePages > 1 ? <div className="menu-option-group" data-nav-group="row" data-nav-key="episode-pages"><button data-focusable disabled={episodePage === 0} onClick={() => setEpisodePage((value) => Math.max(0, value - 1))}>Previous 40</button><span>Page {episodePage + 1} / {episodePages}</span><button data-focusable disabled={episodePage >= episodePages - 1} onClick={() => setEpisodePage((value) => Math.min(episodePages - 1, value + 1))}>Next 40</button></div> : null}
            </div>
          ) : null}

          {menu === 'chapters' ? (
            <div className="player-menu-list" data-nav-group="col" data-nav-key="player-chapters">
              {chapters.map((chapter, index) => <button data-focusable data-autofocus={index === 0 ? '' : undefined} onClick={() => { requestSeek(chapter.start); closeMenu(); showFeedback(chapter.title) }} key={`${chapter.start}:${chapter.title}`}><span><strong>{chapter.title}</strong><small>{formatTime(chapter.start)}</small></span><Play size="1.5rem" /></button>)}
            </div>
          ) : null}

          {menu === 'stats' ? (
            diagnosticImage ? <div className="diagnostic-qr" data-tv-model={currentTvModel()}><img src={diagnosticImage} alt="Private playback report QR code" /><p>Scan with phone. Report for {currentTvModel()} contains format and TV/runtime evidence, never the media URL.</p><button className="secondary-button" data-focusable data-autofocus onClick={() => setDiagnosticImage('')}>Back to stats</button></div> : <><div className="player-stats-grid">
              <span>Source<strong>{prepared?.infoHash ? 'Torrent via server' : 'Direct stream'}</strong></span>
              <span>Video<strong>{videoElement?.videoWidth || 0} × {videoElement?.videoHeight || 0}</strong></span>
              <span>Ready<strong>{Math.round(bufferedAhead)} sec</strong></span>
              <span>Download<strong>{formatBytesPerSecond(stats.downloadSpeed)}</strong></span>
              <span>Peers<strong>{stats.peers || 0}</strong></span>
              <span>Frames<strong>{playbackQuality?.droppedVideoFrames || 0} dropped / {playbackQuality?.totalVideoFrames || 0}</strong></span>
              <span>HDR<strong>{streamHdr || 'SDR'} · {streamHdrSupport}</strong></span>
              <span>Playback<strong>{playbackRate}× · {picture}</strong></span>
            </div><button className="primary-button diagnostic-button" data-focusable onClick={buildDiagnosticQr}>Show private report QR</button></>
          ) : null}

          {menu === 'speed' ? <OptionMenu options={[0.75, 1, 1.25, 1.5]} selected={playbackRate} label={(value) => `${value}×`} onSelect={chooseRate} navKey="speed-options" /> : null}
          {menu === 'picture' ? <OptionMenu options={['fit', 'fill', 'zoom'] as PictureMode[]} selected={picture} label={(value) => value === 'fit' ? 'Fit screen' : value === 'fill' ? 'Fill screen' : 'Cinema zoom'} onSelect={setPicture} navKey="picture-options" /> : null}
          {menu === 'sleep' ? <OptionMenu options={[0, 15, 30, 60, 'end'] as SleepChoice[]} selected={sleep} label={(value) => value === 0 ? 'Off' : value === 'end' ? 'End of episode' : `${value} minutes`} onSelect={chooseSleep} navKey="sleep-options" /> : null}
        </div>
      ) : null}
    </div>
  )
}

function OptionMenu<T extends string | number>({ options, selected, label, onSelect, navKey }: {
  options: T[]
  selected: T
  label: (value: T) => string
  onSelect: (value: T) => void
  navKey: string
}) {
  return (
    <div className="player-menu-list" data-nav-group="col" data-nav-key={navKey}>
      {options.map((value, index) => (
        <button className={selected === value ? 'selected' : ''} data-focusable data-autofocus={selected === value || index === 0 ? '' : undefined} onClick={() => onSelect(value)} key={String(value)}>
          <span>{label(value)}</span>{selected === value ? <Check size="1.5rem" /> : null}
        </button>
      ))}
    </div>
  )
}
